import { TableFilter } from "@neonumy/ant-design";
import { useGetWindowHeight } from "@neonumy/neonumy-ui";
import { Col, Layout, Row, Space, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFileExport } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import TooltipButton from "views/Documents/components/TooltipButton";
import CreateClient from "../Client/components/CreateClient";
import useListClients from "../hooks/clients/useListClients";
import useClientsApi from "../hooks/useClientsApi";
import useClientsTableColumns from "../hooks/useClientsTableColumns";

export default function DataTable(): React.ReactElement {
  const { t } = useTranslation();
  const windowHeight = useGetWindowHeight();
  const { companyId } = useParams();

  const [pageSize, setPageSize] = useState(10);
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { getClientsXlsx } = useClientsApi();

  const [filters, setFilters] = useState<Record<string, any>>({
    name_Icontains: undefined,
    vatNumber_Startswith: undefined,
    code_Istartswith: undefined,
  });

  const { fetchClients, loading, clientsList, totalCount, handleSort } = useListClients({
    pageSize,
    offset,
    filters,
  });

  const COLUMNS = useClientsTableColumns(fetchClients);

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    setOffset((page - 1) * size);
  };

  const handleSearchFilters = (confirm: () => void) => {
    confirm();
    setCurrentPage(1);
    setOffset(0);
  };

  const handleResetFilter = (resetedFilter: Record<string, any>, confirm: () => void) => {
    confirm();
    setCurrentPage(1);
    setOffset(0);

    setFilters(filters => ({ ...filters, ...resetedFilter }));
  };

  const handleOnChangeTable = (
    _pagination: any,
    _filters: Record<string, any>,
    sorter: Record<string, any>,
    extra: Record<string, any>,
  ) => {
    if (extra.action === "sort") {
      handleSort(sorter?.columnKey, sorter?.order);
    }
  };

  const handleExportDocuments = async () => {
    const response = await getClientsXlsx({
      variables: {
        filter: {
          companyId,
          ...filters,
        },
      },
    });

    const url = response?.data?.getClientsXlsx;

    if (url) {
      const link = document.createElement("a");
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Layout className="layout-card">
      <Content className="layout-card-content">
        <Typography.Title className="documents-title" level={4}>
          {t("Clients")}
        </Typography.Title>
        <div className="table-header-actions">
          <div className="selection-info"></div>
          <Space className="actions-container">
            <Row justify="start" gutter={[4, 16]}>
              <Col>
                <TooltipButton
                  placement="left"
                  title={t("Download")}
                  handleOnClick={handleExportDocuments}
                  icon={<FaFileExport />}
                />
              </Col>
            </Row>
          </Space>
        </div>
        <TableFilter
          className="antd-newrai-table"
          scroll={{ y: windowHeight - 450 }}
          dataSource={clientsList}
          loading={loading}
          columns={COLUMNS}
          onChange={handleOnChangeTable}
          pagination={{
            onChange: handlePagination,
            showSizeChanger: true,
            current: currentPage,
            pageSize,
            total: totalCount,
            showTotal: total => t("Total table items", { count: total }),
          }}
          defaultSearchFilter={handleSearchFilters}
          defaultResetFilter={handleResetFilter}
          defaultFiltersObject={filters}
          defaultFilterSet={setFilters}
          debounced={true}
        />
        <CreateClient refetch={fetchClients} />
      </Content>
    </Layout>
  );
}
